<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ArrowsAiCustomToolsAPI from 'dashboard/api/arrowsAi/customTools';
import { toToolPayload } from 'dashboard/composables/useArrowsAiTools';
import ToolParamList from './ToolParamList.vue';

const props = defineProps({
  tool: { type: Object, default: () => ({}) },
  assistantId: { type: Number, default: null },
  submitLabel: { type: String, required: true },
});

const emit = defineEmits(['submit', 'cancel']);
const { t } = useI18n();
const isTesting = ref(false);
const testResult = ref(null);

const state = reactive({
  name: '',
  description: '',
  endpoint: '',
  httpMethod: 'GET',
  authType: 'none',
  authToken: '',
  authHeader: 'X-Api-Key',
  enabled: true,
  paramSchema: [],
});

watch(
  () => props.tool,
  next => {
    state.name = next?.name || '';
    state.description = next?.description || '';
    state.endpoint = next?.endpoint || '';
    state.httpMethod = next?.httpMethod || 'GET';
    state.authType = next?.authType || 'none';
    state.authToken = '';
    state.authHeader = next?.authHeader || 'X-Api-Key';
    state.enabled = next?.enabled !== false;
    state.paramSchema = (next?.paramSchema || []).map(item => ({ ...item }));
    testResult.value = null;
  },
  { immediate: true }
);

const methodOptions = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
];

const authOptions = computed(() => [
  { value: 'none', label: t('ARROWS_AI.TOOLS.AUTH_NONE') },
  { value: 'bearer', label: t('ARROWS_AI.TOOLS.AUTH_BEARER') },
  { value: 'api_key', label: t('ARROWS_AI.TOOLS.AUTH_API_KEY') },
]);

const needsSecret = computed(() => state.authType !== 'none');
const isEdit = computed(() => Boolean(props.tool?.id));

const canTest = computed(
  () =>
    Boolean(props.assistantId) &&
    state.endpoint.trim().toLowerCase().startsWith('https://')
);

const testResultLabel = computed(() => {
  if (!testResult.value) return '';
  if (!testResult.value.status) return testResult.value.error;
  if (testResult.value.ok) {
    return t('ARROWS_AI.TOOLS.TEST_OK', { status: testResult.value.status });
  }

  return t('ARROWS_AI.TOOLS.TEST_FAIL', { status: testResult.value.status });
});

const handleSubmit = () => {
  emit('submit', { ...state });
};

const handleTest = async () => {
  if (!canTest.value) return;

  isTesting.value = true;
  testResult.value = null;
  try {
    const payload = toToolPayload(state, { includeToken: true });
    if (props.tool?.id) payload.id = props.tool.id;
    const { data } = await ArrowsAiCustomToolsAPI.test({
      assistantId: props.assistantId,
      payload,
    });
    const ok = data.status >= 200 && data.status < 300;
    testResult.value = {
      ok,
      status: data.status,
      body: data.body || '',
      error: data.error || '',
    };
  } catch (error) {
    testResult.value = {
      ok: false,
      error: error.response?.data?.error || t('ARROWS_AI.TOOLS.TEST_ERROR'),
    };
  } finally {
    isTesting.value = false;
  }
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input v-model="state.name" :label="t('ARROWS_AI.TOOLS.NAME')" />
    <Input
      v-model="state.description"
      :label="t('ARROWS_AI.TOOLS.DESCRIPTION_LABEL')"
    />
    <Input
      v-model="state.endpoint"
      :label="t('ARROWS_AI.TOOLS.ENDPOINT')"
      :message="t('ARROWS_AI.TOOLS.ENDPOINT_HINT')"
    />
    <div class="flex flex-col gap-1">
      <span class="text-sm text-n-slate-12">{{
        t('ARROWS_AI.TOOLS.METHOD')
      }}</span>
      <Select v-model="state.httpMethod" :options="methodOptions" />
    </div>
    <div class="flex flex-col gap-1">
      <span class="text-sm text-n-slate-12">{{
        t('ARROWS_AI.TOOLS.AUTH')
      }}</span>
      <Select v-model="state.authType" :options="authOptions" />
    </div>
    <Input
      v-if="needsSecret"
      v-model="state.authToken"
      type="password"
      autocomplete="new-password"
      :label="t('ARROWS_AI.TOOLS.AUTH_TOKEN')"
      :message="isEdit ? t('ARROWS_AI.TOOLS.AUTH_TOKEN_HINT') : ''"
    />
    <Input
      v-if="state.authType === 'api_key'"
      v-model="state.authHeader"
      :label="t('ARROWS_AI.TOOLS.AUTH_HEADER')"
    />
    <div class="flex items-center justify-between">
      <span class="text-sm text-n-slate-12">{{
        t('ARROWS_AI.TOOLS.ENABLED')
      }}</span>
      <Switch v-model="state.enabled" />
    </div>
    <ToolParamList v-model="state.paramSchema" />
    <div class="flex flex-col gap-2">
      <Button
        type="button"
        variant="faded"
        color="slate"
        icon="i-lucide-play"
        :label="t('ARROWS_AI.TOOLS.TEST')"
        :is-loading="isTesting"
        :disabled="isTesting || !canTest"
        @click="handleTest"
      />
      <p class="text-xs text-n-slate-11">
        {{ t('ARROWS_AI.TOOLS.TEST_HINT') }}
      </p>
      <div
        v-if="testResult"
        class="flex flex-col gap-2 px-3 py-2 text-xs rounded-lg"
        :class="
          testResult.ok
            ? 'bg-n-teal-2 text-n-teal-11'
            : 'bg-n-ruby-2 text-n-ruby-11'
        "
      >
        <div class="flex items-center gap-2">
          <span
            class="size-3.5 shrink-0"
            :class="
              testResult.ok ? 'i-lucide-check-circle' : 'i-lucide-x-circle'
            "
          />
          {{ testResultLabel }}
        </div>
        <div
          v-if="testResult.body"
          class="max-h-40 overflow-auto break-all text-n-slate-12"
        >
          {{ testResult.body }}
        </div>
        <p v-else-if="testResult.error && testResult.status">
          {{ testResult.error }}
        </p>
      </div>
    </div>
    <div class="flex items-center w-full gap-3">
      <Button
        type="button"
        variant="faded"
        color="slate"
        class="w-full"
        :label="t('ARROWS_AI.CANCEL')"
        @click="emit('cancel')"
      />
      <Button type="submit" class="w-full" :label="submitLabel" />
    </div>
  </form>
</template>
